/**
 * Offer version lifecycle outbox event schemas.
 *
 * Defines the payload shape for:
 * - `offer.version.accepted` — emitted when a client accepts an offer version
 *   (authenticated acceptance or anonymous acceptance via a public link)
 * - `offer.version.rejected` — emitted when a client rejects an offer version
 *   (authenticated rejection or anonymous rejection via a public link)
 *
 * Traceability: REQ-7.18 -> in-app-notifications (offer acceptance/rejection)
 */
import { z } from 'zod';

/**
 * Append-only event type string for offer version acceptance.
 * Downstream consumers rely on this exact value — renaming it is forbidden.
 */
export const OFFER_VERSION_ACCEPTED_EVENT_TYPE = 'offer.version.accepted' as const;

export const OfferVersionAcceptedEventSchema = z
  .object({
    versionId: z.number().int().positive(),
    offerId: z.number().int().positive(),
    // null for anonymous acceptance via a public link
    acceptedBy: z.number().int().positive().nullable(),
    // present only for anonymous acceptance via a public link
    publicLinkId: z.number().int().positive().optional(),
    acceptedAt: z.string().datetime(),
  })
  .strict();

export type OfferVersionAcceptedEvent = z.infer<typeof OfferVersionAcceptedEventSchema>;

/**
 * Append-only event type string for offer version rejection.
 * Downstream consumers rely on this exact value — renaming it is forbidden.
 */
export const OFFER_VERSION_REJECTED_EVENT_TYPE = 'offer.version.rejected' as const;

export const OfferVersionRejectedEventSchema = z
  .object({
    versionId: z.number().int().positive(),
    offerId: z.number().int().positive(),
    // null for anonymous rejection via a public link
    rejectedBy: z.number().int().positive().nullable(),
    // present only for anonymous rejection via a public link
    publicLinkId: z.number().int().positive().optional(),
    rejectionReason: z.string().nullable(),
    rejectedAt: z.string().datetime(),
  })
  .strict();

export type OfferVersionRejectedEvent = z.infer<typeof OfferVersionRejectedEventSchema>;
